// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState } from "react";
import { Mail, Bell, Pencil, Trash2, Loader2 } from "lucide-react";

const EmailConfigurationCard = ({ email, onEdit, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const getIcon = (type) => {
    if (type === "customer") {
      return <Mail className="w-5 h-5 text-green-600 dark:text-green-400" />;
    } else if (type === "admin") {
      return <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    } else {
      return <Mail className="w-5 h-5 text-green-600 dark:text-green-400" />;
    }
  };

  const getIconBg = (type) => {
    if (type === "customer") {
      return "bg-green-100 dark:bg-green-900/30";
    } else if (type === "admin") {
      return "bg-blue-100 dark:bg-blue-900/30";
    } else {
      return "bg-green-100 dark:bg-green-900/30";
    }
  };

  const getDescription = (type) => {
    if (type === "customer") {
      return "Bestätigung an Kunden";
    } else if (type === "admin") {
      return "Benachrichtigung an Admin";
    } else {
      return "E-Mail Benachrichtigung";
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        `Möchten Sie das E-Mail-Template "${email.notification_name}" wirklich löschen?`,
      )
    ) {
      setIsDeleting(true);
      try {
        await onDelete(email);
      } catch (error) {
        console.error("Error deleting email:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg sm:p-6 p-4 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 ${getIconBg(email.recipient_type)} rounded-lg`}>
            {getIcon(email.recipient_type)}
          </div>
          <div>
            <h4 className="font-medium text-slate-900 dark:text-slate-100">
              {email.notification_name}
            </h4>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {getDescription(email.recipient_type)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              email.is_enabled
                ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                : "bg-gray-100 dark:bg-slate-600 text-gray-800 dark:text-slate-300"
            }`}
          >
            {email.is_enabled ? "Aktiv" : "Inaktiv"}
          </span>
          <button
            className="p-1 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
            onClick={() => onEdit(email)}
            title="Bearbeiten"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            className="p-1 text-red-400 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
            onClick={handleDelete}
            title="Löschen"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 grid-cols-1 md:gap-4 gap-2 text-sm mb-4">
        <div>
          <span className="text-slate-500 dark:text-slate-400">
            Template Code:
          </span>
          <span className="ml-2 font-medium text-slate-900 dark:text-slate-200">
            {email.template_code}
          </span>
        </div>
        <div>
          <span className="text-slate-500 dark:text-slate-400">Empfänger:</span>
          <span className="ml-2 font-medium text-slate-900 dark:text-slate-200">
            {email.recipient_type}
          </span>
        </div>
        <div>
          <span className="text-slate-500 dark:text-slate-400">
            Verzögerung:
          </span>
          <span className="ml-2 font-medium text-slate-900 dark:text-slate-200">
            {email.delay_hours}h
          </span>
        </div>
        <div>
          <span className="text-slate-500 dark:text-slate-400">Status:</span>
          <span className="ml-2 font-medium text-green-600 dark:text-green-400">
            {email.is_enabled ? "Aktiv" : "Inaktiv"}
          </span>
        </div>
      </div>
      {email.custom_recipients && email.custom_recipients.length > 0 && (
        <div className="mb-4">
          <span className="text-slate-500 dark:text-slate-400 text-sm gap-1 flex flex-wrap">
            Zusätzliche Empfänger:{" "}
            {email.custom_recipients.map((recipient, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full"
              >
                {recipient}
              </span>
            ))}
          </span>
        </div>
      )}
    </div>
  );
};

export default EmailConfigurationCard;
